var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { DocumentRetrievalFailureReason, isFacilitatedFhirDocumentRetrievalAuthType, matchesDirectRecordFastenInput, } from "healthex-common/document.retrieval.dtos";
import { isErr, isOk, voidResult } from "healthex-common/result";
import { asEnum } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
const DOCUMENT_RETRIEVAL_RECORDS_REUSED_EVENT = "DocumentRetrievalRecordsReused";
export class DocumentRetrievalManager {
    get retrieval() {
        return this._retrieval;
    }
    get patientId() {
        var _a;
        return (_a = this.authManager.id) !== null && _a !== void 0 ? _a : "";
    }
    constructor(documentRetrievalService, authManager, analyticsService) {
        this.documentRetrievalService = documentRetrievalService;
        this.authManager = authManager;
        this.analyticsService = analyticsService;
        this.pollingTimerId = null;
        this.pendingRetrievalKeys = new Set();
        this._retrieval = null;
        this.retrievalErrors = [];
        this.specificLocationResult = null;
        this.specificLocationExists = false;
        this.specificLocationAddedAt = null;
        this.specificLocationResultLoaded = false;
        this.checkingForFinalization = false;
        reaction(() => this.retrieval, () => {
            if (this.waitingForFinalization()) {
                this.startPollingTimerIfNeeded();
            }
            else {
                this.clearPollingTimerIfNeeded();
            }
        }, { fireImmediately: true });
    }
    resetRetrieval() {
        this._retrieval = null;
        this.retrievalErrors = [];
        this.specificLocationResult = null;
        this.specificLocationResultLoaded = false;
        this.specificLocationExists = false;
        this.specificLocationAddedAt = null;
        this.pendingRetrievalKeys.clear();
    }
    markSpecificLocationResultLoaded() {
        this.specificLocationResultLoaded = true;
    }
    setRetrievalErrors(errors) {
        this.retrievalErrors = errors;
    }
    async getExistingInProgressDocumentRetrieval() {
        const batch = await this.getExistingRetrieval(undefined, false);
        if (!batch)
            return null;
        await this.updateBatch(batch);
        return batch;
    }
    async initiateDocumentRetrieval(studyId, useTefca, addRecordLocationsDirectly = undefined) {
        const retrievalKey = `${this.patientId}-${studyId !== null && studyId !== void 0 ? studyId : "null"}-${addRecordLocationsDirectly ? "addRecordLocationsDirectly" : "normal"}`;
        if (this.pendingRetrievalKeys.has(retrievalKey)) {
            return null;
        }
        this.pendingRetrievalKeys.add(retrievalKey);
        let batchResult;
        try {
            batchResult = await this.documentRetrievalService.retrievePatientData(this.patientId, {
                studyId,
                useTefca,
                addRecordLocationsDirectly,
            });
        }
        catch (error) {
            console.error(`Error initiating document retrieval: ${error}`);
            this.setTEFCAErrors([DocumentRetrievalFailureReason.GENERIC_ERROR]);
            this.pendingRetrievalKeys.delete(retrievalKey);
            return null;
        }
        let batch = null;
        if (isErr(batchResult)) {
            this.pendingRetrievalKeys.delete(retrievalKey);
            const errorBatch = await this.getExistingInProgressDocumentRetrieval();
            batch = errorBatch;
        }
        else {
            batch = batchResult;
        }
        if (!batch) {
            console.error(`Error initiating document retrieval: No batch found`);
            this.setTEFCAErrors([DocumentRetrievalFailureReason.GENERIC_ERROR]);
            this.pendingRetrievalKeys.delete(retrievalKey);
            return null;
        }
        const tefcaErrors = batch.tefcaQueries
            .filter((query) => query.error)
            .map((query) => {
            var _a, _b, _c;
            const errorStr = (_b = (_a = query.error) === null || _a === void 0 ? void 0 : _a.replace(/^ForbiddenException: /, "")) !== null && _b !== void 0 ? _b : "";
            return (_c = asEnum(DocumentRetrievalFailureReason)(errorStr)) !== null && _c !== void 0 ? _c : DocumentRetrievalFailureReason.GENERIC_ERROR;
        });
        if (tefcaErrors.length > 0) {
            this.setTEFCAErrors(tefcaErrors);
        }
        await this.updateBatch(batch);
        this.pendingRetrievalKeys.delete(retrievalKey);
        return batch.batchId;
    }
    async retryTEFCARetrieval(batchId) {
        const batch = await this.refreshBatch(batchId);
        if (!batch)
            return;
        this.clearTEFCAErrors();
        if (batch.tefcaQueries.some((query) => !query.isComplete)) {
            void this.startTEFCARetrieval(batch.batchId);
        }
    }
    async skipTEFCARetrieval(batchId) {
        const batch = await this.refreshBatch(batchId);
        if (!batch)
            return;
        this.clearTEFCAErrors();
        const updatedBatch = await this.documentRetrievalService.skipTEFCARetrieval(this.patientId, batchId);
        if (isOk(updatedBatch)) {
            await this.updateBatch(updatedBatch);
        }
    }
    async getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords) {
        return await this.documentRetrievalService.getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords);
    }
    async getDownloadPdfLink(batchId) {
        return await this.documentRetrievalService.getTaskBatchDownloadPdfLink(this.patientId, batchId);
    }
    startPollingTimerIfNeeded() {
        this.clearPollingTimerIfNeeded();
        this.pollingTimerId = window.setInterval(() => {
            void this.checkForFinalization();
        }, 3000);
    }
    clearPollingTimerIfNeeded() {
        if (this.pollingTimerId !== null) {
            window.clearInterval(this.pollingTimerId);
            this.pollingTimerId = null;
        }
    }
    clearPollingTimerIfStartedByFastenCallback() {
        this.clearPollingTimerIfNeeded();
    }
    waitingForFinalization() {
        const retrieval = this.retrieval;
        if (!retrieval)
            return false;
        return retrieval.documentSelectionComplete && !retrieval.finalized;
    }
    async refreshBatch(batchId) {
        if (!batchId) {
            console.warn(`Batch id is not valid`);
            return null;
        }
        const batch = await this.documentRetrievalService.getTaskBatch(this.patientId, batchId);
        if (!batch) {
            console.warn(`Batch ${batchId} not found`);
            return null;
        }
        await this.updateBatch(batch);
        return batch;
    }
    async checkForFinalization() {
        if (!this.retrieval)
            return;
        if (this.checkingForFinalization)
            return;
        this.checkingForFinalization = true;
        this.authManager.recordApiActivity();
        try {
            const finalized = await this.documentRetrievalService.getTaskBatchFinalized(this.patientId, this.retrieval.batchId);
            if (finalized) {
                await this.refreshBatch(this.retrieval.batchId);
            }
        }
        finally {
            this.checkingForFinalization = false;
        }
    }
    async getExistingRetrieval(batchId, documentSelectionComplete) {
        return await this.documentRetrievalService.getExistingRetrieval(this.patientId, {
            batchId,
            documentSelectionComplete,
        });
    }
    async updateBatch(batch) {
        var _a;
        if (!batch)
            return;
        if (((_a = this._retrieval) === null || _a === void 0 ? void 0 : _a.batchId) !== batch.batchId) {
            console.log(`[Document Retrieval] New retrieval batch id: ${batch.batchId}`);
            this.clearTEFCAErrors();
        }
        this._retrieval = batch;
    }
    async startTEFCARetrieval(batchId) {
        const batch = await this.documentRetrievalService.startTEFCARetrieval(this.patientId, batchId);
        if (isErr(batch)) {
            this.setTEFCAErrors([batch.error]);
        }
        else {
            await this.updateBatch(batch);
        }
    }
    setTEFCAErrors(errors) {
        this.retrievalErrors = errors;
    }
    clearTEFCAErrors() {
        this.retrievalErrors = [];
    }
    async getFhirAuthorizeUrl(batchId, endpoint, redirectUrl) {
        return await this.documentRetrievalService.getFhirAuthorizeUrl(this.patientId, batchId, endpoint, redirectUrl);
    }
    async startFhirRetrievalWithCode(batchId, locationId, endpoint, redirectUri, code, codeVerifier) {
        const result = await this.documentRetrievalService.startFhirRetrieval(this.patientId, batchId, {
            endpoint,
            code,
            redirectUri,
            locationId,
            codeVerifier,
        });
        if (isErr(result))
            return result;
        if (result.batch) {
            await this.updateBatch(result.batch);
        }
        return voidResult();
    }
    async getSuggestedFastenEndpoints(batchId) {
        return await this.documentRetrievalService.getSuggestedFastenEndpoints(this.patientId, batchId);
    }
    async startFastenRetrievalWithConnectionId(batchId, connectionId, portalId, brandId, endpointId, source) {
        const result = await this.documentRetrievalService.startFastenRetrieval(this.patientId, batchId, {
            connectionId,
            portalId,
            brandId,
            endpointId,
            source,
        });
        if (result.batch) {
            await this.updateBatch(result.batch);
        }
    }
    async startFhirRetrievalWithExistingCredentials(batchId, endpoint) {
        const result = await this.documentRetrievalService.startFhirRetrieval(this.patientId, batchId, {
            endpoint,
        });
        if (isErr(result))
            throw new Error(`Unexpected error starting FHIR retrieval with existing credentials: ${result.error}`);
        if (result.batch) {
            await this.updateBatch(result.batch);
        }
        return result;
    }
    async startFastenRetrievalWithExistingConnection(batchId, brandId, portalId, endpointId, source) {
        const result = await this.documentRetrievalService.startFastenRetrieval(this.patientId, batchId, {
            brandId,
            portalId,
            endpointId,
            source,
        });
        if (result.batch) {
            await this.updateBatch(result.batch);
        }
        return result;
    }
    async skipLocation(batchId, locationId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            skipLocationId: locationId,
        });
        await this.updateBatch(batch);
    }
    async updateExcludedLocations(batchId, excludedLocationIds) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            excludedLocationIds,
        });
        await this.updateBatch(batch);
    }
    async doneSelectingDocuments(batchId, studyId, createdBatch) {
        var _a, _b, _c, _d, _e;
        if (((_a = this._retrieval) === null || _a === void 0 ? void 0 : _a.initiatingStudyId) !== studyId) {
            console.warn(`[Document Retrieval] Study ID mismatch: ${(_c = (_b = this._retrieval) === null || _b === void 0 ? void 0 : _b.initiatingStudyId) !== null && _c !== void 0 ? _c : "null"} !== ${studyId}`);
        }
        await this.documentRetrievalService.createDocumentDelivery(this.patientId, {
            batchId,
            initiatingStudyId: (_e = (_d = this._retrieval) === null || _d === void 0 ? void 0 : _d.initiatingStudyId) !== null && _e !== void 0 ? _e : studyId,
            createdBatch,
        });
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            documentSelectionComplete: true,
        });
        await this.updateBatch(batch);
        heap.addEventProperties({ globalNewPatient: "false", projectNewPatient: "false" });
    }
    async reuseExistingDocuments(existingBatch, studyId) {
        await this.documentRetrievalService.createDocumentDelivery(this.patientId, {
            batchId: existingBatch.batchId,
            initiatingStudyId: studyId,
            createdBatch: null,
        });
        void this.analyticsService.trackHeapEvent({
            event: DOCUMENT_RETRIEVAL_RECORDS_REUSED_EVENT,
            identity: this.patientId,
            properties: {
                ...getHeapGlobalEventProperties(),
            },
        });
    }
    async downloadInitiated(batchId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            patientHasDownloaded: true,
        });
        await this.updateBatch(batch);
    }
    async getDownloadDocumentsLink(batchId) {
        return await this.documentRetrievalService.getTaskBatchDownloadLink(this.patientId, batchId);
    }
    async getMostRecentDownloadableOrProcessingTaskBatch() {
        return await this.documentRetrievalService.getMostRecentDownloadableOrProcessingTaskBatch(this.patientId);
    }
    async getPatientTEFCAAuthInfo() {
        return await this.documentRetrievalService.getPatientTEFCAAuthInfo(this.patientId);
    }
    findAddedDirectRecordLocationId(input) {
        const batch = this._retrieval;
        if (!batch) {
            return undefined;
        }
        const match = batch.locations.find((location) => {
            var _a;
            return matchesDirectRecordFastenInput(location.organizationMetadata, {
                brandId: (_a = input.brandId) !== null && _a !== void 0 ? _a : "",
                portalId: input.portalId,
                endpointId: input.endpointId,
            });
        });
        return match === null || match === void 0 ? void 0 : match.id;
    }
    async checkIfLocationExists(batchId, input) {
        return this.documentRetrievalService.checkIfLocationExists(this.patientId, batchId, input);
    }
    async resolveSpecificLocationExists(batchId) {
        var _a, _b;
        const matchedLocation = (_a = this.specificLocationResult) === null || _a === void 0 ? void 0 : _a.matchedLocation;
        if (!matchedLocation || !matchedLocation.isPrevious) {
            this.specificLocationExists = false;
            this.specificLocationAddedAt = null;
            return;
        }
        this.specificLocationExists = true;
        try {
            const existing = await this.checkIfLocationExists(batchId, this.buildLocationCandidate(matchedLocation));
            this.specificLocationAddedAt = (_b = existing.addedAt) !== null && _b !== void 0 ? _b : null;
        }
        catch (error) {
            console.warn(`Error resolving addedAt for already-added specific location: ${error}`);
            this.specificLocationAddedAt = null;
        }
    }
    clearSpecificLocationExists() {
        this.specificLocationExists = false;
        this.specificLocationAddedAt = null;
    }
    buildLocationCandidate(location) {
        var _a;
        const metadata = location.organizationMetadata;
        const fhirEndpoint = metadata && isFacilitatedFhirDocumentRetrievalAuthType(metadata.authType) && "fhirEndpoint" in metadata
            ? metadata.fhirEndpoint
            : undefined;
        return {
            name: (_a = location.organizationName) !== null && _a !== void 0 ? _a : undefined,
            fhirEndpoint,
            hcid: location.hcid,
        };
    }
    async refreshDirectRecordLocation(batchId, locationId) {
        var _a;
        const result = await this.documentRetrievalService.refreshDirectRecordLocation(this.patientId, batchId, locationId);
        if (result.batch) {
            await this.updateBatch(result.batch);
        }
        if (result.locationId && result.locationId !== locationId && ((_a = this.specificLocationResult) === null || _a === void 0 ? void 0 : _a.matchedLocation.id) === locationId) {
            this.specificLocationResult = {
                ...this.specificLocationResult,
                matchedLocation: { ...this.specificLocationResult.matchedLocation, id: result.locationId, isPrevious: false },
            };
        }
        return result;
    }
    matchDirectRecordLocationInCurrentRetrieval(input) {
        const batch = this._retrieval;
        if (!batch) {
            return null;
        }
        return this.setSpecificLocationResultFromBatch(batch, input);
    }
    async matchDirectRecordLocationForBatch(batchId, input) {
        let batch = this._retrieval;
        if (!batch || batch.batchId !== batchId) {
            batch = await this.refreshBatch(batchId);
        }
        if (!batch) {
            return null;
        }
        return this.setSpecificLocationResultFromBatch(batch, input);
    }
    setSpecificLocationResultFromBatch(batch, input) {
        const matchedLocation = this.findFastenLocationOnBatch(batch, input);
        if (!matchedLocation) {
            return null;
        }
        const result = {
            batch,
            matchedLocation,
            locationCreated: false,
        };
        this.specificLocationResult = result;
        return result;
    }
    findFastenLocationOnBatch(batch, input) {
        return batch.locations.find((location) => matchesDirectRecordFastenInput(location.organizationMetadata, input));
    }
    async addDirectRecordLocation(batchId, input) {
        const result = await this.documentRetrievalService.addDirectRecordLocation(this.patientId, batchId, input);
        if (isOk(result)) {
            this.specificLocationResult = result;
            await this.updateBatch(result.batch);
        }
        else {
            this.specificLocationResult = null;
        }
        return result;
    }
}
__decorate([
    observable
], DocumentRetrievalManager.prototype, "_retrieval", void 0);
__decorate([
    computed
], DocumentRetrievalManager.prototype, "retrieval", null);
__decorate([
    observable
], DocumentRetrievalManager.prototype, "retrievalErrors", void 0);
__decorate([
    observable
], DocumentRetrievalManager.prototype, "specificLocationResult", void 0);
__decorate([
    observable
], DocumentRetrievalManager.prototype, "specificLocationExists", void 0);
__decorate([
    observable
], DocumentRetrievalManager.prototype, "specificLocationAddedAt", void 0);
__decorate([
    observable
], DocumentRetrievalManager.prototype, "specificLocationResultLoaded", void 0);
__decorate([
    computed
], DocumentRetrievalManager.prototype, "patientId", null);
